import {
  useState,
} from 'react';
import {
  useGoogleLogin,
  GoogleOAuthProvider,
} from '@react-oauth/google';
import {
  useAlerts,
  useAuthentication,
} from '@dinosave/core/stores';
import type {
  UserEntity,
} from '@dinosave/core/types';
import Button from '@/ui/components/Button';
import Text from '@/ui/components/Text';

import styles from './GoogleButton.module.css';

type AuthenticatedResponse = {
  user: UserEntity;
  token: string;
}

type GoogleButtonProps = {
  onSuccess?: (response: AuthenticatedResponse) => void;
}

const GoogleButton = (props: GoogleButtonProps) => {
  // ##########################################################################
  // PROPS
  // ##########################################################################

  const {
    onSuccess,
  } = props;

  // ##########################################################################
  // STORES
  // ##########################################################################

  const addError = useAlerts(s => s.addError);

  const googleAuthentication = useAuthentication(s => s.googleAuthentication);

  // ##########################################################################
  // STATE
  // ##########################################################################

  const [loading, setLoading] = useState(false);

  // ##########################################################################
  // FUNCTIONS
  // ##########################################################################

  const handleGoogleAuth = useGoogleLogin({
    onSuccess: async (data) => {
      const {
        access_token,
      } = data;

      try {
        const authResponse = await googleAuthentication(access_token);

        if (
          onSuccess
        ) onSuccess(authResponse);
      } catch {
        setLoading(false);
      }
    },
    onError: () => {
      setLoading(false);

      const error = new Error('Failed authentication via Google');
      addError(error);
    },
  });

  const handleClick = () => {
    setLoading(true);
    handleGoogleAuth();
  };

  // ##########################################################################
  // RENDER
  // ##########################################################################

  return (
    <div className={styles.googleButton}>
      <Button
        color="secondary"
        onClick={handleClick}
        loading={loading}
        roundness="round"
        centered
      >
        <img
          src="/images/socials/google.svg"
          alt="Login via Google"
        />
        <Text
          kind="control"
          size="regular"
        >
          Continue with Google
        </Text>
      </Button>
    </div>
  );
};

const GoogleButtonWithProvider = (props: GoogleButtonProps) => (
  <GoogleOAuthProvider clientId={import.meta.env.PUBLIC_ENV__GOOGLE_OAUTH_CLIENT_ID}>
    <GoogleButton {...props} />
  </GoogleOAuthProvider>
);

export default GoogleButtonWithProvider;
